/**
 * Cross-user asset mirroring — client side (Collab Phase 4).
 *
 * While a SHARED project is open and the user is signed in, this module:
 *  - installs `store.assets.onStored` so every new local asset uploads to the
 *    project's R2 mirror (idempotent PUT, 3× backoff, silent terminal)
 *  - runs an activation-time PRESENCE SWEEP: every pb-asset:// id referenced
 *    by the state is verified/uploaded (covers assets stored before the
 *    project was shared and the local hash-dedupe reuse path)
 *  - installs the renderer's remote fetcher (`asset-remote.js`): a missing
 *    local record is fetched from the server, reconstructed from the
 *    attribution headers, persisted with notify:false (no upload echo), and
 *    announced via `collab:asset-fetched {id}` so the shell can repaint.
 *
 * Solo projects: `deactivate()` leaves everything exactly as before —
 * onStored null, no fetcher, no network.
 */

import { eventBus } from '../core/events.js';
import { store } from '../core/state.js';
import { setRemoteAssetFetcher } from '../core/asset-remote.js';
import { reportError } from '../core/report-error.js';
import {
  getProjectAsset,
  isRealUser,
  listProjectMembers,
  putProjectAsset
} from '../templates/auth-api.js';
import { TemplateApiError } from '../templates/template-api.js';
import { ASSET_META_HEADERS } from '../shared/collab-protocol.js';

const PB_ASSET_ID_RE = /pb-asset:\/\/([a-f0-9-]+)/g;
const NEGATIVE_CACHE_TTL_MS = 30_000;
const RETRY_DELAYS_MS = [250, 1000, 3000];

let _active = false;
let _projectId = null;
let _uploaded = new Set();
let _uploadInflight = new Map();
let _fetchInflight = new Map();
let _negativeCache = new Map();
let _lifecycleGeneration = 0;
let _activationRequest = 0;
// Injectable timing for tests
let _now = () => Date.now();
let _delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export function isCollabAssetsActive() {
  return _active;
}

function decodeHeader(resp, name) {
  const raw = resp.headers.get(name);
  if (!raw) return undefined;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

/** Collect every pb-asset:// id referenced anywhere in the state. */
export function collectReferencedAssetIds(state) {
  const ids = new Set();
  if (!state) return ids;
  let json;
  try {
    json = JSON.stringify(state);
  } catch {
    return ids;
  }
  PB_ASSET_ID_RE.lastIndex = 0;
  let match;
  while ((match = PB_ASSET_ID_RE.exec(json)) !== null) ids.add(match[1]);
  return ids;
}

function isTerminalUploadError(err) {
  // 4xx = the server made a decision (no access, conflict, too large) —
  // retrying identical bytes cannot change it. Network errors (status 0) and
  // 5xx are retryable.
  return err instanceof TemplateApiError && err.status >= 400 && err.status < 500;
}

/**
 * Idempotent upload of one asset. Resolves true when the server has the
 * asset (fresh PUT or dedupe), false on terminal failure (already reported).
 */
export async function ensureUploaded(id) {
  if (!_active || !_projectId || !id) return false;
  if (_uploaded.has(id)) return true;
  if (_uploadInflight.has(id)) return _uploadInflight.get(id);

  const projectId = _projectId;
  const generation = _lifecycleGeneration;
  const inflight = _uploadInflight;
  const uploaded = _uploaded;
  const task = (async () => {
    const record = await store.assets.get(id).catch(() => null);
    if (!_active || _projectId !== projectId || _lifecycleGeneration !== generation) return false;
    if (!record || !record.blob) return false;
    let lastError = null;
    for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
      // Deactivation (project switch/sign-out) aborts quietly mid-queue.
      if (!_active || _projectId !== projectId || _lifecycleGeneration !== generation) return false;
      try {
        await putProjectAsset(projectId, id, record.blob, {
          mime: record.mime,
          hash: record.hash,
          filename: record.filename,
          source: record.source,
          author: record.author,
          authorUrl: record.authorUrl
        });
        if (!_active || _projectId !== projectId || _lifecycleGeneration !== generation)
          return false;
        uploaded.add(id);
        return true;
      } catch (err) {
        lastError = err;
        if (isTerminalUploadError(err)) break;
        if (attempt < RETRY_DELAYS_MS.length) await _delay(RETRY_DELAYS_MS[attempt]);
      }
    }
    // Silent terminal: the editing session must not surface toasts for a
    // background mirror; peers fall back to the GET retry path meanwhile.
    if (_active && _projectId === projectId && _lifecycleGeneration === generation) {
      reportError('CollabAssets upload', lastError, {
        user: 'silent',
        detail: `asset ${id} upload to ${projectId} failed terminally`
      });
    }
    return false;
  })().finally(() => {
    // Never let an old project's completion delete a same-id task belonging
    // to the newly active project.
    if (_uploadInflight === inflight && inflight.get(id) === task) inflight.delete(id);
  });
  _uploadInflight.set(id, task);
  return task;
}

/** Activation-time sweep: verify/upload every referenced asset. */
export async function sweepProjectAssets() {
  if (!_active) return;
  const projectId = _projectId;
  const generation = _lifecycleGeneration;
  const state = store.getState();
  if (state?.projectId !== projectId) return;
  const ids = collectReferencedAssetIds(state);
  for (const id of ids) {
    if (!_active || _projectId !== projectId || _lifecycleGeneration !== generation) return;
    await ensureUploaded(id);
  }
}

/**
 * The renderer's remote fetcher. In-flight dedupe + 30 s negative cache live
 * HERE (single owner of remote-asset traffic); the renderer hook is a thin
 * call. Success: record persisted (notify:false) + `collab:asset-fetched`.
 */
export async function fetchRemoteAsset(id) {
  if (!_active || !_projectId || !id) return null;
  const negativeUntil = _negativeCache.get(id);
  if (negativeUntil && _now() < negativeUntil) return null;
  if (negativeUntil) _negativeCache.delete(id);
  if (_fetchInflight.has(id)) return _fetchInflight.get(id);

  const projectId = _projectId;
  const generation = _lifecycleGeneration;
  const inflight = _fetchInflight;
  const uploaded = _uploaded;
  const negativeCache = _negativeCache;
  const task = (async () => {
    try {
      const resp = await getProjectAsset(projectId, id);
      if (!_active || _projectId !== projectId || _lifecycleGeneration !== generation) return null;
      const blob = await resp.blob();
      if (!_active || _projectId !== projectId || _lifecycleGeneration !== generation) return null;
      const meta = {
        filename: decodeHeader(resp, ASSET_META_HEADERS.filename) || `${id}.bin`,
        mime: resp.headers.get('Content-Type') || blob.type || 'application/octet-stream',
        source: decodeHeader(resp, ASSET_META_HEADERS.source),
        author: decodeHeader(resp, ASSET_META_HEADERS.author),
        authorUrl: decodeHeader(resp, ASSET_META_HEADERS.authorUrl),
        hash: decodeHeader(resp, ASSET_META_HEADERS.hash)
      };
      // notify:false — a remote fetch must never echo back into the upload
      // queue (the server obviously already has it).
      await store.assets.storeWithId(id, blob, meta, { notify: false });
      if (!_active || _projectId !== projectId || _lifecycleGeneration !== generation) return null;
      uploaded.add(id);
      eventBus.emit('collab:asset-fetched', { id });
      return { blob, ...meta };
    } catch (err) {
      // Covers in-flight uploads on the other side: negative-cache 30 s, the
      // next render pass retries.
      if (_active && _projectId === projectId && _lifecycleGeneration === generation) {
        negativeCache.set(id, _now() + NEGATIVE_CACHE_TTL_MS);
      }
      if (
        _active &&
        _projectId === projectId &&
        _lifecycleGeneration === generation &&
        !(err instanceof TemplateApiError)
      ) {
        reportError('CollabAssets fetch', err, { user: 'silent' });
      }
      return null;
    } finally {
      if (_fetchInflight === inflight && inflight.get(id) === task) inflight.delete(id);
    }
  })();
  _fetchInflight.set(id, task);
  return task;
}

/** Install hooks + sweep for a shared project. Idempotent per projectId. */
export function activateCollabAssets(projectId) {
  if (_active && _projectId === projectId) return;
  deactivateCollabAssets();
  _activationRequest += 1;
  _lifecycleGeneration += 1;
  _active = true;
  _projectId = projectId;
  store.assets.onStored = ({ id }) => {
    void ensureUploaded(id);
  };
  setRemoteAssetFetcher((id) => fetchRemoteAsset(id));
  void sweepProjectAssets();
}

export function deactivateCollabAssets() {
  _activationRequest += 1;
  _lifecycleGeneration += 1;
  _active = false;
  _projectId = null;
  _uploaded = new Set();
  _uploadInflight = new Map();
  _fetchInflight = new Map();
  _negativeCache = new Map();
  store.assets.onStored = null;
  setRemoteAssetFetcher(null);
}

/**
 * Decide whether mirroring should be on: signed-in real user + current
 * project cloud-synced + actually shared (member count > 1, or the caller is
 * a member). Called by the shell on auth/project/membership changes.
 */
export async function maybeActivateCollabAssets(currentUser) {
  const request = ++_activationRequest;
  const initialState = store.getState();
  const projectId = initialState?.projectId;
  if (!isRealUser(currentUser) || !projectId || initialState.cloudVersion == null) {
    deactivateCollabAssets();
    return false;
  }
  try {
    const resp = await listProjectMembers(projectId);
    if (
      request !== _activationRequest ||
      store.getState() !== initialState ||
      store.getState()?.projectId !== projectId
    )
      return false;
    const members = Array.isArray(resp?.members) ? resp.members : [];
    const selfIsMember = members.some(
      (m) => m.sub === String(currentUser.sub) && m.role !== 'owner'
    );
    const shared = members.length > 1 || selfIsMember;
    if (shared) {
      activateCollabAssets(projectId);
    } else {
      deactivateCollabAssets();
    }
    return shared;
  } catch {
    // 404 (not cloud-saved / no access) or transient network — stay/solo off.
    if (request === _activationRequest && store.getState() === initialState)
      deactivateCollabAssets();
    return false;
  }
}

/** Test hook: reset module state + inject fake timing. */
export function _resetCollabAssetsForTests({ now, delay } = {}) {
  deactivateCollabAssets();
  _now = now || (() => Date.now());
  _delay = delay || ((ms) => new Promise((resolve) => setTimeout(resolve, ms)));
}
